
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import logger from 'utils/logger';
import * as API from 'api/queries/index';
import { GetServerSideProps } from 'next';
import { getTokenOptServer } from '../../auth/user/AuthUtils.bs';
import createApolloClient from 'api/graphql/client';

import { default as ProductPageComponent, ProductPageProps } from 'components/pages/store/ProductPage';

// Utils
import skuPresentationResolver from 'utils/skuPresentationResolver';

// Models
import { WebConfig } from 'models/webConfig.model';
import { SkuPresentation, SkuTheme, SkuTemplate } from 'models/Models';
import { Categories } from 'models/category.model';

// Legacy
import { EnvUtil } from 'common/util.bs';

import { Card } from 'models/Models.bs';
import { Nullable } from 'models/types';
import { getStoreCategory } from 'api/graphql/CmsData.bs';
import {
  GetShortDescriptionDocument,
  GetShortDescriptionQuery,
  GetShortDescriptionQueryVariables,
} from 'api/graphql/generated';
import { PRODUCT_SSR_PAGE_CACHE } from 'common/constants';
import CategoryPage, { CategoryPageProps } from 'components/pages/store/CategoryPage';

const ProductPage = (props: ProductPageProps | CategoryPageProps) =>
  !('inventory' in props) ? <ProductPageComponent {...props} /> : <CategoryPage {...props} />;

const errorMessages = {
  webConfig: 'Failed to fetch Web Config',
  skuBySlug: 'Failed to fetch SKU by Slug',
  giftThemes: 'Failed to fetch Gift Themes',
  personalisedMessages: 'Failed to fetch Personalised Messages',
  inventory: 'Failed to fetch Inventory',
  categories: 'Failed to fetch Categories',
  zagBuyingPrompt: 'Failed to fetch zag buying prompt data',
};

const getWebConfig = async () => {
  try {
    return await API.webConfig();
  } catch (error) {
    logger.error(
      {
        err: error,
        source: 'Config API',
        journey: 'Purchase',
      },
      `error: ${errorMessages.webConfig}`
    );
    throw new Error(errorMessages.webConfig);
  }
};

const getSkuBySlug = async (slug: string, tokenOptServer: string) => {
  try {
    return await API.skuBySlug(slug, tokenOptServer);
  } catch (error) {
    logger.error(
      {
        err: error,
        slug,
        source: 'SKU by Slug API',
        journey: 'Purchase',
      },
      `error: ${errorMessages.skuBySlug}`
    );

    throw new Error(errorMessages.skuBySlug);
  }
};

const getGiftThemes = async (code: string) => {
  try {
    return await API.giftThemes(code);
  } catch (error) {
    logger.error(
      {
        giftThemeCode: code,
        err: error,
        source: 'Gift Themes API',
        journey: 'Purchase',
      },
      `error: ${errorMessages.giftThemes}`
    );

    throw new Error(errorMessages.giftThemes);
  }
};

const getPersonalisedMessages = async (language: string, isPreview: boolean) => {
  try {
    return await API.personalisedMessages(language, isPreview);
  } catch (error) {
    logger.error(
      {
        err: error,
        source: 'CMS',
        journey: 'Purchase',
      },
      `error: ${errorMessages.personalisedMessages}`
    );

    throw new Error(errorMessages.personalisedMessages);
  }
};

const getInventory = async (tokenOptServer: string) => {
  try {
    return await API.inventory(tokenOptServer);
  } catch (error) {
    logger.error(
      {
        err: error,
        source: 'Inventory API',
        journey: 'Purchase',
      },
      `error: ${errorMessages.inventory}`
    );

    throw new Error(errorMessages.inventory);
  }
};

const getCategories = async () => {
  try {
    return await API.categories();
  } catch (error) {
    logger.error(
      {
        err: error,
        source: 'Categories API',
        journey: 'Purchase',
      },
      `error: ${errorMessages.categories}`
    );

    throw new Error(errorMessages.categories);
  }
};

export const getServerSideProps: GetServerSideProps = async context => {
  const language = EnvUtil.getCmsLanguage();
  const isPreview = context.query?.preview !== undefined;

  try {
    const configs: Nullable<WebConfig> = await getWebConfig();
    const pid = context.query?.pid as string;
    const isGiftCard = pid.includes('gift-card');
    const slug = isGiftCard ? pid.slice(0, pid.lastIndexOf('-gift-card')) : pid;
    const cookies = context.req.headers.cookie;

    if (isGiftCard) {
      let sku = await getSkuBySlug(slug, getTokenOptServer(cookies));
      const { cmsId, code } = sku;
      const giftThemes = await getGiftThemes(code);

      const personalisedMessages = await getPersonalisedMessages(language, isPreview);

      let skuPresentation: Nullable<SkuPresentation> = null;

      if (cmsId) {
        const data: Nullable<SkuPresentation> = await skuPresentationResolver(cmsId, context);

        if (data) {
          skuPresentation = data;
          if (skuPresentation.soldOut.items[0]?._system_.codename === 'yes') {
            return {
              redirect: {
                permanent: false,
                destination: '/store',
              },
            };
          }

          // Modify sku themes and templates with original image url from CMS for image optimization
          const themesObj: { [code: string]: SkuTheme } = Object.fromEntries(
            skuPresentation.themes.items.map(item => [item.code !== '' ? item.code : item._system_.codename, item])
          );

          const templatesObj: { [uid: string]: SkuTemplate } = Object.fromEntries(
            skuPresentation.themesTemplate.items.map(item => [item.uid !== '' ? item.uid : item._system_.id, item])
          );

          sku.themes = sku.themes.map(item => {
            const cmsTheme = themesObj[item.code];
            if (cmsTheme) {
              return { ...item, cmsImage: cmsTheme.image };
            }
            return item;
          });

          sku.templates = sku.templates.map(item => {
            const cmsTemplate = templatesObj[item.uid];
            if (cmsTemplate) {
              return {
                ...item,
                cmsImagePreview: cmsTemplate.imagePreview,
                cmsImageFrame: cmsTemplate.imageFrame,
              };
            }
            return item;
          });
        }

        const { data: shortDescriptionRes } = await (
          await createApolloClient(isPreview)
        ).query<GetShortDescriptionQuery, GetShortDescriptionQueryVariables>({
          query: GetShortDescriptionDocument,
          variables: {
            codename: cmsId,
            languageCodename: language,
          },
        });

        if (shortDescriptionRes?.skuPresentation?.descriptionShort) {
          sku = {
            ...sku,
            descriptionShort: shortDescriptionRes.skuPresentation.descriptionShort,
          };
        }
      }

      let zagBuyingPromptData = null;

      if (sku?.layerEnabled && sku?.layerType === 'default') {
        try {
          zagBuyingPromptData = await (await API.zagBuyingPrompt(language, isPreview))?.item;
        } catch (error) {
          logger.error(
            {
              err: error,
              source: 'CMS API',
              journey: 'Purchase',
            },
            `error: ${errorMessages.zagBuyingPrompt}`
          );

          throw new Error(errorMessages.zagBuyingPrompt);
        }
      }

      const props: ProductPageProps = {
        sku: sku || null,
        configs: configs || null,
        skuPresentation: skuPresentation || null,
        giftThemes: giftThemes || null,
        personalisedMessages: personalisedMessages || null,
        zagBuyingPrompt: zagBuyingPromptData,
      };

      if (!isPreview) {
        context.res.setHeader('Cache-Control', PRODUCT_SSR_PAGE_CACHE);
      }

      return {
        props,
      };
    } else {
      // Handles store category pages (ie /store/tech)
      // Logic for this is in the old product page component
      const inventory = await getInventory(getTokenOptServer(cookies));
      const categories = await getCategories();
      let storeCategory = null;

      // Converting inventory to old format
      const convertedInventory = {
        ...inventory,
        skus: inventory.skus.map(sku => ({
          ...sku,
          isPbCategoryHidden: sku.hideFromAllBusinessCategories,
          isPcCategoryHidden: sku.hideFromAllClassicCategories,
        })),
      };

      if (categories) {
        const category = (categories as Categories).results.find(c => c.slug === slug);
        const codename = category?.cmsId;

        if (codename) {
          try {
            const storeCategoryData = await getStoreCategory(language, isPreview, codename);

            if (storeCategoryData) {
              if (storeCategoryData.top_content) {
                storeCategory = await API.modifyStoreCategory(JSON.stringify(storeCategoryData), isPreview, language);
              } else {
                storeCategory = JSON.stringify(storeCategoryData);
              }
            }
          } catch (error) {
            logger.error(
              {
                err: error,
                source: 'CMS',
                journey: 'Purchase',
              },
              'Failed to fetch category data'
            );
          }
        }
      }

      if (!isPreview) {
        context.res.setHeader('Cache-Control', PRODUCT_SSR_PAGE_CACHE);
      }

      if (storeCategory) {
        return {
          props: {
            pid: pid,
            inventory: convertedInventory ? convertedInventory.skus.map(card => Card.skuToCard(card)) : null,
            categories: categories || null,
            storeCategory,
          },
        };
      }

      return {
        redirect: {
          permanent: false,
          destination: '/store',
        },
      };
    }
  } catch (error) {
    const errorIsHandled = Object.values(errorMessages).find(msg => msg === error.message);

    if (!errorIsHandled) {
      logger.error(
        {
          err: error,
          journey: 'Purchase',
        },
        'Failed to fetch product data'
      );
    }

    return {
      redirect: {
        permanent: false,
        destination: '/store',
      },
    };
  }
};

export default ProductPage;
